import type { SwitchableStore } from "../data/switchable.js";
import { unsentText, type UnsentLine } from "../data/unsent.js";
import type { List } from "../lib/api.js";
import { el } from "../lib/dom.js";
import { titled } from "../lib/environment.js";
import { icon } from "../lib/icons.js";
import { sharedPath, type Route } from "../lib/router.js";
import type { SharedVisit, VisitState } from "../lib/sharedVisit.js";
import type { ShareVisitor } from "../lib/sharing.js";
import type { CompletedVisibility } from "./completed.js";
import { listScreen } from "./list.js";
import type { ListPolicy } from "./listPolicy.js";
import type { MenuItem } from "./menu.js";
import { nameGate, type NameGate } from "./nameGate.js";
import { banner, messageOf, syncLine, type Screen, type ScreenHost } from "./ui.js";

/**
 * A shared list's own page (g72): the list, as its link allows, and nothing
 * of anybody's account. The rows, Details, Add and the completed rules are
 * the owner's list screen's, under a policy that says what a link may do.
 */

/** The page's words, exported so the tests pin them. */
export const SHARED_WORDS = {
  view: "View only",
  edit: "Can edit",
  sharedBy: "Shared by ",
  byYou: "Shared by you",
  withYou: "Shared with you",
  yourLists: "Your lists",
  addToLists: "Add to my Lists",
  addedToLists: "Added to your Lists.",
  removeFromLists: "Remove from my Lists",
  signIn: "Sign in again",
  addingAs: (name: string) => `Adding as ${name}`,
  changeName: "Change name",
  ended: "This link no longer works. You can still read what's here, but changes won't be saved.",
  unsent: (n: number) => `Changes to ${n} ${n === 1 ? "item weren't" : "items weren't"} sent.`,
  showUnsent: "Show unsent changes",
  copyAll: "Copy all",
  copiedUnsent: "Unsent changes copied.",
  copyUnsentManually: "Select the text and copy it.",
  unsentField: "Unsent changes",
  remove: "Remove from this device",
  loading: "Opening shared list",
  unavailableTitle: "This link isn't available",
  unavailable: "It may have been turned off or replaced, or the list was deleted. Ask the person who shared it for a new link.",
  unreachableTitle: "Can't open this list",
  unreachable: "Can't reach Trayflux, and this list isn't saved on this device yet.",
  limitedTitle: "Can't open this list right now",
  limited: "Too many attempts from this network. Try again in a few minutes.",
  nostorageTitle: "This browser won't keep the list",
  nostorage: "Trayflux needs to store the list on this device to show it. Allow site data, or try another browser.",
  signinTitle: "Sign in to open this list",
  signin: "Sign in to the account that added it to its Lists.",
  signInButton: "Sign in",
  retry: "Try again",
} as const;

/**
 * Who shared the list, as the viewer should read it: the owner's name, "you"
 * on the owner's own visit, or nobody in particular when the owner has no
 * name. Never an email: only a display name reaches this page. The list's
 * creator is its owner, since only the owning account ever creates a list.
 */
export type Sharer = { name: string } | "you" | "unnamed";

export function sharerOf(store: Pick<SwitchableStore, "creator">, list: Pick<List, "creatorId"> | undefined, viewer: string | null | undefined): Sharer {
  const owner = list?.creatorId;
  if (owner && viewer && owner === viewer) return "you";
  const name = owner ? store.creator(owner)?.name : undefined;
  return name ? { name } : "unnamed";
}

/** "Shared by Andre", "Shared by you" or "Shared with you". */
export function sharedPhrase(sharer: Sharer): string {
  return sharer === "you" ? SHARED_WORDS.byYou : sharer === "unnamed" ? SHARED_WORDS.withYou : `${SHARED_WORDS.sharedBy}${sharer.name}`;
}

/** What the tab says: the list's name and who shared it, or what the page is, and never the link. */
export function sharedTitle(list: Pick<List, "name"> | undefined, state?: VisitState["kind"], sharer?: Sharer): string {
  if (!list) return titled(state === "unavailable" ? "Link not available" : "Shared list");
  if (!sharer) return titled(list.name);
  // Mid-sentence, so only the first word drops its capital: never the name.
  const phrase = sharedPhrase(sharer);
  return titled(`${list.name}, ${phrase.charAt(0).toLowerCase()}${phrase.slice(1)}`);
}

/**
 * Completed rows start collapsed on a shared page, and opening them lasts
 * for the visit (g72): nothing is stored, and the owner's own preference is
 * never read or written.
 */
export function visitCompleted(): CompletedVisibility {
  let hidden = true;
  return {
    get hidden() { return hidden; },
    // The page's own rule, not a default waiting on a choice: its control
    // is offered as a chosen filter's is.
    chosen: true,
    set(next) { hidden = next; },
  };
}

/** A page with no list to show: loading, a dead link, no connection, no storage, or an added list opened signed out. */
export function sharedStateScreen(state: Exclude<VisitState["kind"], "open">, retry: () => void): Screen {
  document.title = sharedTitle(undefined, state);
  const again = el("button", { class: "secondary", text: SHARED_WORDS.retry, attrs: { type: "button" }, on: { click: retry } });
  const words: Record<typeof state, Node[]> = {
    loading: [el("h1", { class: "visually-hidden", text: SHARED_WORDS.loading }), el("p", { class: "muted", text: "Loading…" })],
    unavailable: [el("h1", { text: SHARED_WORDS.unavailableTitle }), el("p", { class: "muted", text: SHARED_WORDS.unavailable })],
    unreachable: [el("h1", { text: SHARED_WORDS.unreachableTitle }), el("p", { class: "muted", text: SHARED_WORDS.unreachable }), again],
    limited: [el("h1", { text: SHARED_WORDS.limitedTitle }), el("p", { class: "muted", text: SHARED_WORDS.limited }), again],
    nostorage: [el("h1", { text: SHARED_WORDS.nostorageTitle }), el("p", { class: "muted", text: SHARED_WORDS.nostorage })],
    signin: [el("h1", { text: SHARED_WORDS.signinTitle }), el("p", { class: "muted", text: SHARED_WORDS.signin }),
      el("button", { text: SHARED_WORDS.signInButton, attrs: { type: "button" }, on: { click: retry } })],
  };
  return { node: el("section", { class: "screen centered shared-state", children: words[state] }), dispose() {} };
}

/** What a state screen says, as one sentence or two for a screen reader. */
export function sharedStateWords(state: Exclude<VisitState["kind"], "open">): string {
  switch (state) {
    case "loading": return SHARED_WORDS.loading;
    case "unavailable": return `${SHARED_WORDS.unavailableTitle}. ${SHARED_WORDS.unavailable}`;
    case "unreachable": return `${SHARED_WORDS.unreachableTitle}. ${SHARED_WORDS.unreachable}`;
    case "limited": return `${SHARED_WORDS.limitedTitle}. ${SHARED_WORDS.limited}`;
    case "nostorage": return `${SHARED_WORDS.nostorageTitle}. ${SHARED_WORDS.nostorage}`;
    case "signin": return `${SHARED_WORDS.signinTitle}. ${SHARED_WORDS.signin}`;
  }
}

/**
 * One live region for the states a link passes through before its list opens
 * (g72). Each state is a screen of its own that replaces the last, and a
 * region inside one would be new each time, so nothing would be heard after
 * Try again. This one stays outside them, and is emptied before each state
 * is said so that the same state twice is heard twice. The frame that opens
 * with the list has its own region.
 */
export function sharedStatus(): { node: HTMLElement; say(state: Exclude<VisitState["kind"], "open">): void; dispose(): void } {
  const node = el("p", { class: "visually-hidden", attrs: { role: "status" } });
  let timer: ReturnType<typeof setTimeout> | undefined;
  return {
    node,
    say(state) {
      node.textContent = "";
      clearTimeout(timer);
      timer = setTimeout(() => { node.textContent = sharedStateWords(state); }, 100);
    },
    dispose() {
      clearTimeout(timer);
      node.remove();
    },
  };
}

export interface SharedPageOptions {
  /** Shows sign-in over the page, for a visitor whose session expired. */
  signIn(): void;
  /** The link opens nothing any more and its copy is gone: the page says so. */
  gone(): void;
  /** Shows sign-in over the page for a guest's Add to my Lists, held across it (g132). */
  signInToAdd(): void;
  /**
   * The list is in the signed-in visitor's Lists, by an earlier Add or by the
   * one just made (`justAdded`): the link's page hands over to the account's
   * app, which opens it as one of their lists (g137).
   */
  listed?(justAdded: boolean): void;
  /**
   * Opened from the account's Lists, inside its own app (g137): nothing above
   * the title, since the tabs are the way back, and the list's menu offers
   * Remove from my Lists.
   */
  inApp?: { remove(): void };
}

/** The list a link opens, drawn in a frame with no account. */
export function sharedListScreen(host: ScreenHost, visit: SharedVisit, store: SwitchableStore, options: SharedPageOptions): Screen {
  const listId = store.share().listId || visit.scope()?.listId || "";
  const visitor = (): ShareVisitor | undefined => visit.visitor();
  const editLink = () => store.share().permission === "edit";

  const gate: NameGate = nameGate(host, {
    visitor: () => {
      const who = visitor();
      return { kind: who?.kind ?? "anonymous", name: who?.name ?? null, ...(who?.sessionExpired ? { sessionExpired: true } : {}) };
    },
    // Named, and an edit link that works. The name is asked of the server's
    // own answer too: a signed-in account with no name has a scope that
    // looks writable until its first write is refused.
    ready: () => store.share().writable && !(visitor()?.needsName ?? false) && !visitor()?.sessionExpired,
    editable: () => editLink() && store.share().state !== "ended",
    name: (displayName) => visit.name(displayName),
    signIn: options.signIn,
  });

  // Under the title: whose list this is and what the link allows (g72,
  // after Linda could not tell whose list she had opened). The name sits in a
  // `bdi`, as on the rows, so a right-to-left name keeps "Shared by" first.
  const byline = el("p", { class: "shared-byline" });
  const viewer = () => visitor()?.creatorId ?? visit.scope()?.visitor ?? null;
  const sharer = () => sharerOf(store, store.list(listId), viewer());
  // Every shared sync announces the scope, so this runs about every ten
  // seconds while the page is open: it redraws only when the words change,
  // or a selection in the line would be lost on each refresh.
  let painted = "";
  const paintByline = () => {
    const who = sharer();
    const words = `${typeof who === "object" ? `name:${who.name}` : who}|${editLink() ? "edit" : "view"}`;
    if (words === painted) return;
    painted = words;
    // The access words stay together: a long name breaks before them, never inside them.
    const access = el("span", { class: "shared-byline-access", text: `· ${editLink() ? SHARED_WORDS.edit : SHARED_WORDS.view}` });
    if (typeof who === "object") byline.replaceChildren(SHARED_WORDS.sharedBy, el("bdi", { text: who.name }), " ", access);
    else byline.replaceChildren(sharedPhrase(who), " ", access);
  };

  const policy: ListPolicy = {
    owner: false,
    controls: () => (editLink() && store.share().state !== "ended" ? "edit" : "view"),
    onControls: (fn) => store.onShare(() => fn()),
    completed: visitCompleted(),
    // Every row but the visitor's own says who added it.
    attribution: () => ({ viewer: viewer() }),
    gate,
    menu: (): MenuItem[] => {
      const name = visitor()?.name;
      // Someone not signed in adds by signing in (g132). A signed-in visitor
      // has the button above the list instead, so the menu never repeats it.
      const guestAdd = visitor()?.kind !== "account" && visit.scope()?.credential.kind === "link" && visit.listed() === "addable";
      return [
        ...(name ? [{ caption: SHARED_WORDS.addingAs(name) }] : []),
        { label: SHARED_WORDS.changeName, icon: "edit", onSelect: () => gate.changeName(menuButton()) },
        ...(guestAdd ? [{ label: SHARED_WORDS.addToLists, icon: "plus" as const, onSelect: () => void addAsGuest() }] : []),
        ...(options.inApp ? [{ label: SHARED_WORDS.removeFromLists, icon: "trash" as const, onSelect: options.inApp.remove }] : []),
      ];
    },
    // Inside the app a reader can still take the list out of their Lists (g137).
    ...(options.inApp ? { viewMenu: (): MenuItem[] => [{ label: SHARED_WORDS.removeFromLists, icon: "trash", onSelect: options.inApp!.remove }] } : {}),
    byline,
    paths: {
      list: () => sharedPath(),
      item: (id) => sharedPath(id),
      selection: (route: Route) => (route.name === "shared" ? { itemId: route.itemId } : undefined),
    },
  };

  const list = listScreen(host, listId, policy);
  const menuButton = () => list.node.querySelector<HTMLElement>(".list-options-open") ?? undefined;

  // Above the list: the way back to a signed-in visitor's own lists, and
  // beside it Add to my Lists, out in the open (g132); or a way back into
  // their account when its sign-in expired. Once the list is in their Lists
  // the account's app has it (g137), and inside that app nothing is here.
  // A guest's Add is in the list's menu, but a view link has no menu, so a
  // guest holding one gets the same button here.
  const top = el("p", { class: "shared-top" });
  let adding = false;
  // Every shared sync repaints this, about every ten seconds: only a change
  // redraws it, or focus on the button would be lost on each refresh.
  let paintedTop: string | undefined;
  const paintTop = () => {
    const who = visitor();
    const menuless = !editLink() && store.share().state !== "ended";
    const state = `${who?.kind}|${who?.sessionExpired ?? false}|${visit.listed()}|${menuless}`;
    if (state === paintedTop) return;
    paintedTop = state;
    if (who?.sessionExpired) {
      top.replaceChildren(el("button", { class: "secondary", text: SHARED_WORDS.signIn, attrs: { type: "button" }, on: { click: options.signIn } }));
    } else if (options.inApp) {
      top.replaceChildren();
    } else if (who?.kind === "account") {
      // A plain link, not the router's: it loads the account's app from the start.
      const yours = el("a", { class: "shared-your-lists", text: SHARED_WORDS.yourLists, attrs: { href: "/" } });
      // Only what the server says: the owner reads "owner", and with no answer yet nothing is offered.
      const listed = visit.listed();
      if (listed === "added" && options.listed) {
        top.replaceChildren();
        options.listed(false);
      } else {
        top.replaceChildren(yours, ...(listed === "addable" ? [el("button", { class: "secondary shared-add", text: SHARED_WORDS.addToLists,
          attrs: { type: "button" }, on: { click: () => void add() } })] : []));
      }
    } else if (menuless && visit.scope()?.credential.kind === "link" && visit.listed() === "addable") {
      top.replaceChildren(el("button", { class: "secondary shared-add", text: SHARED_WORDS.addToLists, attrs: { type: "button" },
        on: { click: () => void addAsGuest() } }));
    } else {
      top.replaceChildren();
    }
    top.hidden = top.childElementCount === 0;
  };

  /**
   * A signed-in visitor's Add: the page hands over to their app, which opens
   * the list as theirs and says it was added (g137). With nowhere to hand
   * over to, the button goes and focus moves to the way to their Lists.
   */
  const add = async () => {
    if (adding) return;
    adding = true;
    try {
      await visit.add();
      if (options.listed) {
        options.listed(true);
        return;
      }
      paintTop();
      top.querySelector<HTMLElement>(".shared-your-lists")?.focus();
      host.announce(SHARED_WORDS.addedToLists);
    } catch (error) {
      host.announce(messageOf(error));
      // Owner, signed out since, or full: ask who is looking, and the top repaints.
      void visit.refresh();
    } finally {
      adding = false;
    }
  };

  /**
   * A guest's Add (g132): what they changed is sent first, while this browser
   * still writes as them, then the add is held for the sign-in it opens.
   */
  const addAsGuest = async () => {
    await store.sync().catch(() => undefined);
    try {
      await visit.holdAdd();
    } catch (error) {
      host.announce(messageOf(error));
      return;
    }
    options.signInToAdd();
  };

  // A link that stopped working while open: said once, and the copy can go.
  const ended = el("div", { class: "status shared-ended" });
  let saidEnded = false;
  // The ended notice repaints on every share change, so what the visitor did
  // with the unsent changes is kept here rather than in the markup (g74).
  let unsentOpen = false;
  let unsentSaid = "";
  let unsentManual = false;

  /**
   * Called inside the click, with nothing awaited first: the clipboard wants
   * the click's activation, as the share dialog's Copy does (g73).
   */
  const copyUnsent = (lines: readonly UnsentLine[]) => {
    const text = unsentText(lines);
    // In place, not a repaint, so focus stays on Copy all unless the text
    // has to be selected by hand.
    const done = (said: string, manual: boolean) => {
      unsentSaid = said;
      unsentManual ||= manual;
      const status = ended.querySelector(".shared-unsent-status");
      if (status) status.textContent = said;
      host.announce(said);
      if (!manual) return;
      let field = ended.querySelector<HTMLTextAreaElement>(".shared-unsent-text");
      if (!field) {
        field = unsentField(lines);
        ended.querySelector(".shared-unsent-copy")?.before(field);
      }
      field.focus();
      field.select();
    };
    if (typeof navigator.clipboard?.writeText !== "function") {
      done(SHARED_WORDS.copyUnsentManually, true);
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => done(SHARED_WORDS.copiedUnsent, false),
      () => done(SHARED_WORDS.copyUnsentManually, true),
    );
  };

  /** The lines as one field to select by hand, when the clipboard is out of reach. */
  const unsentField = (lines: readonly UnsentLine[]) => {
    const field = el("textarea", {
      class: "shared-unsent-text",
      attrs: { readonly: true, rows: Math.min(lines.length, 6), spellcheck: "false", "aria-label": SHARED_WORDS.unsentField },
    });
    field.value = unsentText(lines);
    return field;
  };

  /** What never went, one line per item, to read back or copy (g74). */
  const unsentDetails = (lines: readonly UnsentLine[]) => {
    const details = el("details", {
      class: "more-details shared-unsent",
      children: [
        el("summary", {
          class: "more-details-summary",
          children: [el("span", { class: "more-details-label", text: SHARED_WORDS.showUnsent }), icon("chevron")],
        }),
        el("div", {
          class: "more-details-body shared-unsent-body",
          children: [
            el("ul", {
              class: "shared-unsent-list",
              children: lines.map((line) => el("li", {
                children: [el("bdi", { text: line.text }), " ", el("span", { class: "shared-unsent-change", text: line.change })],
              })),
            }),
            ...(unsentManual ? [unsentField(lines)] : []),
            el("button", { class: "secondary shared-unsent-copy", text: SHARED_WORDS.copyAll, attrs: { type: "button" },
              on: { click: () => copyUnsent(lines) } }),
            el("p", { class: "shared-unsent-status", text: unsentSaid }),
          ],
        }),
      ],
    });
    details.open = unsentOpen;
    details.addEventListener("toggle", () => { unsentOpen = details.open; });
    return details;
  };
  const paintEnded = () => {
    const share = store.share();
    if (share.state !== "ended") {
      ended.replaceChildren();
      return;
    }
    const unsent = store.unsent().size;
    const words = unsent ? `${SHARED_WORDS.ended} ${SHARED_WORDS.unsent(unsent)}` : SHARED_WORDS.ended;
    const lines = store.unsentChanges();
    ended.replaceChildren(
      banner(words),
      ...(lines.length ? [unsentDetails(lines)] : []),
      el("button", { class: "secondary", text: SHARED_WORDS.remove, attrs: { type: "button" }, on: { click: () => {
        void store.discard().then(options.gone, options.gone);
      } } }),
    );
    if (!saidEnded) {
      saidEnded = true;
      host.announce(words);
    }
  };

  const footer = syncLine(store, "page");

  const paintTitle = () => {
    document.title = sharedTitle(store.list(listId), undefined, sharer());
  };
  const paintWho = () => {
    paintByline();
    paintTitle();
  };

  const stopShare = store.onShare((share) => {
    paintEnded();
    // Every answer says whether the list is in the visitor's Lists (g132).
    paintTop();
    // Someone else is looking now -- a sign-in or a sign-out in another tab,
    // or a name given there: ask the server who, and move to their scope.
    if (share.state === "held" && share.reason === "visitor_changed") void visit.refresh();
  });
  // The menu names the visitor too, and reads it each time it opens.
  // A visitor who turns out to be the owner reads "Shared by you".
  const stopVisitor = visit.onVisitor(() => {
    paintTop();
    paintWho();
  });
  const stopLists = store.onLists((change) => {
    if (change.id === listId) paintWho();
  });
  // A new name from the owner relabels the line and the tab, as it does their rows.
  const stopCreators = store.onCreators(paintWho);
  // The access, should the page open before the server's first answer.
  const stopAccess = store.onShare(paintByline);
  paintTop();
  paintEnded();
  paintWho();

  const node = el("div", { class: "shared-page", children: [top, ended, list.node, footer.node] });

  return {
    node,
    update: (route) => list.update?.(route),
    action: () => list.action?.(),
    dirty: () => list.dirty?.() ?? false,
    busy: () => list.busy?.() ?? false,
    dispose() {
      stopShare();
      stopVisitor();
      stopLists();
      stopCreators();
      stopAccess();
      footer.dispose();
      list.dispose();
    },
  };
}
